import * as THREE from 'three';

import type { CameraFlyoverConfig, FlyoverOrbitSegment, Vector3Like } from '../sceneConfig';
import type { SceneControls } from '../controls/SceneControls';
import type { CameraDirector } from './CameraDirector';
import { EASING } from '../../utils/easing';

const SKIP_SMOOTH_SPEED = 4;
const SKIP_SMOOTH_MIN_DURATION = 0.8;
const SKIP_SMOOTH_MAX_DURATION = 2.5;

/** Degrees per sample point when converting orbit arcs to spline control points. */
const ORBIT_SAMPLE_STEP_DEG = 20;

/** Number of subdivisions for arc-length sampling on the position curve. */
const ARC_LENGTH_DIVISIONS = 200;

/** Arc-fraction threshold over which the target blend completes (5% of path). */
const TARGET_BLEND_ARC = 0.05;

interface PaceKeyframe {
    time: number; // fraction of total duration (0..1)
    arc: number;  // fraction of total arc length (0..1)
}

interface CameraFlyoverParams {
    camera: THREE.PerspectiveCamera;
    controls: SceneControls;
    cameraDirector: CameraDirector;
    config: CameraFlyoverConfig;
}

export class CameraFlyover {
    private camera: THREE.PerspectiveCamera;
    private controls: SceneControls;
    private cameraDirector: CameraDirector;
    private segments: CameraFlyoverConfig['segments'];
    private mode: 'spline' | 'segments';

    private _active = false;
    private _completed = false;
    private controlsWasEnabled = true;
    private resolvePromise: (() => void) | null = null;

    private delay: number;
    private delayElapsed = 0;

    // ── Shared ──
    private totalDuration = 0;
    private globalElapsed = 0;
    private readonly _pos = new THREE.Vector3();
    private readonly _tgt = new THREE.Vector3();

    // ── Spline mode ──
    private positionCurve: THREE.CatmullRomCurve3 | null = null;
    private targetCurve: THREE.CatmullRomCurve3 | null = null;
    private startTarget = new THREE.Vector3();
    private constantTarget: THREE.Vector3 | null = null;
    private paceKeyframes: PaceKeyframe[] = [];

    // ── Segments mode ──
    private currentSegmentIndex = 0;
    private segmentElapsed = 0;
    private segmentStartPosition = new THREE.Vector3();
    private segmentStartTarget = new THREE.Vector3();
    private segmentStartFov = 75;
    private orbitStartAzimuth = 0;

    constructor(params: CameraFlyoverParams) {
        this.camera = params.camera;
        this.controls = params.controls;
        this.cameraDirector = params.cameraDirector;
        this.segments = params.config.segments;
        this.delay = params.config.delay ?? 0;
        this.mode = params.config.mode ?? 'spline';
    }

    get active(): boolean {
        return this._active;
    }

    get completed(): boolean {
        return this._completed;
    }

    /** Returns the starting pose for PreviewTour.stop() override. */
    getStartPose(): { position: THREE.Vector3; target: THREE.Vector3 } {
        const first = this.segments[0];
        if (first.type === 'orbit') {
            return this.computeOrbitPose(first, 0);
        }
        return {
            position: new THREE.Vector3(first.position.x, first.position.y, first.position.z),
            target: new THREE.Vector3(first.target.x, first.target.y, first.target.z),
        };
    }

    start(): Promise<void> {
        // If a dev-time force-finish (or earlier `skip()`) already marked
        // the flyover complete, a subsequent `start()` from the normal
        // scene-loading path must NOT replay the animation — that would
        // override the user's free-fly camera. Resolve immediately.
        if (this._completed) return Promise.resolve();
        this._active = true;
        this.controlsWasEnabled = this.controls.enabled;
        this.controls.enabled = false;

        if (this.mode === 'spline') {
            this.buildSplinePath();
        } else {
            this.initSegmentMode();
        }
        this.globalElapsed = 0;

        return new Promise<void>((resolve) => {
            this.resolvePromise = resolve;
        });
    }

    /** Returns true while flyover is active. */
    update(deltaTime: number): boolean {
        if (!this._active) {
            return false;
        }

        if (this.delayElapsed < this.delay) {
            this.delayElapsed += deltaTime;
            return true;
        }

        return this.mode === 'spline'
            ? this.updateSpline(deltaTime)
            : this.updateSegments(deltaTime);
    }

    skip(): void {
        if (this._completed) {
            return;
        }

        const { position, target, fov } = this.getFinalPose();
        this.applyPose(position, target, fov);
        this.finish();
    }

    skipSmooth(): void {
        if (this._completed) {
            return;
        }

        this._active = false;

        this.cameraDirector.syncPoseFromCamera();

        const { position, target, fov } = this.getFinalPose();

        const distance = this.camera.position.distanceTo(position);
        const duration = THREE.MathUtils.clamp(
            distance / SKIP_SMOOTH_SPEED,
            SKIP_SMOOTH_MIN_DURATION,
            SKIP_SMOOTH_MAX_DURATION,
        );

        this.cameraDirector.transitionToPose(
            { position, target, fov },
            duration,
            () => {
                this._completed = true;
                this.controls.enabled = this.controlsWasEnabled;
                this.resolvePromise?.();
                this.resolvePromise = null;
            },
            undefined,
            false,
            'ease-in-out',
            false,
        );
    }

    private getFinalPose(): { position: THREE.Vector3; target: THREE.Vector3; fov?: number } {
        const last = this.segments[this.segments.length - 1];
        if (last.type === 'waypoint') {
            return {
                position: new THREE.Vector3(last.position.x, last.position.y, last.position.z),
                target: new THREE.Vector3(last.target.x, last.target.y, last.target.z),
                fov: last.fov,
            };
        }
        const { position, target } = this.computeOrbitPose(last, 1);
        return { position, target, fov: last.fov };
    }

    // ═══════════════════════════════════════════════════════════════════
    // SPLINE MODE
    // ═══════════════════════════════════════════════════════════════════

    private updateSpline(deltaTime: number): boolean {
        this.globalElapsed += deltaTime;
        const rawProgress = Math.min(this.globalElapsed / this.totalDuration, 1);
        const arcFraction = this.computeArcFraction(rawProgress);

        const pos = this.positionCurve!.getPointAt(arcFraction, this._pos);

        let tgt: THREE.Vector3;
        if (this.constantTarget) {
            const blendT = Math.min(arcFraction / TARGET_BLEND_ARC, 1);
            const smooth = blendT * blendT * (3 - 2 * blendT);
            tgt = this._tgt.lerpVectors(this.startTarget, this.constantTarget, smooth);
        } else {
            tgt = this.targetCurve!.getPointAt(arcFraction, this._tgt);
        }

        this.applyPose(pos, tgt);

        if (rawProgress >= 1) {
            this.finish();
        }

        return true;
    }

    /**
     * Samples all segments into control points, builds a smooth CatmullRom
     * position spline, and computes Hermite pacing keyframes.
     */
    private buildSplinePath(): void {
        const posPoints: THREE.Vector3[] = [];
        const tgtPoints: THREE.Vector3[] = [];
        const segBounds: number[] = [0];

        let prevPos = this.camera.position.clone();
        posPoints.push(prevPos.clone());

        this.totalDuration = 0;

        for (const segment of this.segments) {
            this.totalDuration += segment.duration;

            if (segment.type === 'waypoint') {
                const endPos = new THREE.Vector3(segment.position.x, segment.position.y, segment.position.z);
                posPoints.push(endPos);
                tgtPoints.push(new THREE.Vector3(segment.target.x, segment.target.y, segment.target.z));
                prevPos = endPos;
            } else {
                const azStart = segment.startAzimuth
                    ?? Math.atan2(prevPos.z - segment.center.z, prevPos.x - segment.center.x);
                const angleRad = segment.angleDeg * (Math.PI / 180);
                const steps = Math.max(6, Math.ceil(Math.abs(segment.angleDeg) / ORBIT_SAMPLE_STEP_DEG));
                const centerVec = new THREE.Vector3(segment.center.x, segment.center.y, segment.center.z);

                for (let i = 1; i <= steps; i++) {
                    const frac = i / steps;
                    const az = azStart + angleRad * frac;
                    posPoints.push(new THREE.Vector3(
                        segment.center.x + segment.radius * Math.cos(az),
                        segment.center.y + segment.height,
                        segment.center.z + segment.radius * Math.sin(az),
                    ));
                    tgtPoints.push(centerVec.clone());
                }
                prevPos = posPoints[posPoints.length - 1];
            }

            segBounds.push(posPoints.length - 1);
        }

        this.positionCurve = new THREE.CatmullRomCurve3(posPoints, false, 'centripetal');
        this.computeStartTarget(tgtPoints);
        this.buildPaceKeyframes(segBounds, posPoints.length);
    }

    private computeStartTarget(tgtPoints: THREE.Vector3[]): void {
        const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
        const firstSegTarget = tgtPoints[0];
        const dist = this.camera.position.distanceTo(firstSegTarget);
        this.startTarget.copy(this.camera.position).addScaledVector(fwd, dist);

        const allSameTarget = tgtPoints.every(t =>
            Math.abs(t.x - firstSegTarget.x) < 0.001
            && Math.abs(t.y - firstSegTarget.y) < 0.001
            && Math.abs(t.z - firstSegTarget.z) < 0.001,
        );

        if (allSameTarget) {
            this.constantTarget = firstSegTarget;
            this.targetCurve = null;
        } else {
            tgtPoints.unshift(this.startTarget.clone());
            this.constantTarget = null;
            this.targetCurve = new THREE.CatmullRomCurve3(tgtPoints, false, 'centripetal');
        }
    }

    private buildPaceKeyframes(segBounds: number[], totalCP: number): void {
        const arcLengths = this.positionCurve!.getLengths(ARC_LENGTH_DIVISIONS);
        const totalLen = arcLengths[ARC_LENGTH_DIVISIONS];

        let cumTime = 0;
        this.paceKeyframes = [{ time: 0, arc: 0 }];

        for (let i = 0; i < this.segments.length; i++) {
            cumTime += this.segments[i].duration;
            const u = segBounds[i + 1] / (totalCP - 1);
            const arcLen = this.sampleArcLength(arcLengths, u);
            this.paceKeyframes.push({
                time: cumTime / this.totalDuration,
                arc: arcLen / totalLen,
            });
        }
    }

    private sampleArcLength(arcLengths: number[], u: number): number {
        const idx = u * ARC_LENGTH_DIVISIONS;
        const lo = Math.floor(idx);
        if (lo >= ARC_LENGTH_DIVISIONS) return arcLengths[ARC_LENGTH_DIVISIONS];
        const frac = idx - lo;
        return arcLengths[lo] + (arcLengths[lo + 1] - arcLengths[lo]) * frac;
    }

    private computeArcFraction(timeFraction: number): number {
        const kf = this.paceKeyframes;
        if (timeFraction <= 0) return 0;
        if (timeFraction >= 1) return 1;

        let i = 0;
        while (i < kf.length - 2 && kf[i + 1].time < timeFraction) i++;

        const k0 = kf[i];
        const k1 = kf[i + 1];
        const dt = k1.time - k0.time;
        const localT = (timeFraction - k0.time) / dt;

        const slope0 = i === 0
            ? 0
            : (kf[i + 1].arc - kf[i - 1].arc) / (kf[i + 1].time - kf[i - 1].time);
        const slope1 = i === kf.length - 2
            ? 0
            : (kf[i + 2].arc - kf[i].arc) / (kf[i + 2].time - kf[i].time);

        const m0 = slope0 * dt;
        const m1 = slope1 * dt;

        const t2 = localT * localT;
        const t3 = t2 * localT;
        const result = (2 * t3 - 3 * t2 + 1) * k0.arc
            + (t3 - 2 * t2 + localT) * m0
            + (-2 * t3 + 3 * t2) * k1.arc
            + (t3 - t2) * m1;

        return Math.max(0, Math.min(1, result));
    }

    // ═══════════════════════════════════════════════════════════════════
    // SEGMENTS MODE
    // ═══════════════════════════════════════════════════════════════════

    private initSegmentMode(): void {
        this.totalDuration = this.segments.reduce((sum, s) => sum + s.duration, 0);
        this.initSegment(0);
    }

    private updateSegments(deltaTime: number): boolean {
        const segment = this.segments[this.currentSegmentIndex];
        this.segmentElapsed += deltaTime;

        const rawProgress = Math.min(this.segmentElapsed / segment.duration, 1);
        const easing = EASING[segment.easing ?? 'ease-in-out'];
        const easedProgress = easing(rawProgress);

        if (segment.type === 'waypoint') {
            this.updateWaypoint(segment, easedProgress);
        } else {
            this.updateOrbit(segment, easedProgress);
        }

        if (rawProgress >= 1) {
            const excess = this.segmentElapsed - segment.duration;
            this.advanceSegment();
            if (this._active) {
                this.segmentElapsed = excess;
            }
        }

        return true;
    }

    private initSegment(index: number): void {
        this.currentSegmentIndex = index;
        this.segmentElapsed = 0;
        this.segmentStartPosition.copy(this.camera.position);
        this.segmentStartFov = this.camera.fov;

        const segment = this.segments[index];
        if (segment.type === 'orbit') {
            this.orbitStartAzimuth = segment.startAzimuth
                ?? this.computeAzimuth(this.camera.position, segment.center);
            this.segmentStartTarget.set(segment.center.x, segment.center.y, segment.center.z);
        } else {
            const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
            this.segmentStartTarget.copy(this.camera.position).add(dir);
        }
    }

    private updateWaypoint(
        segment: { position: Vector3Like; target: Vector3Like; fov?: number },
        t: number,
    ): void {
        this._pos.set(segment.position.x, segment.position.y, segment.position.z);
        this._tgt.set(segment.target.x, segment.target.y, segment.target.z);

        const pos = new THREE.Vector3().lerpVectors(this.segmentStartPosition, this._pos, t);
        const tgt = new THREE.Vector3().lerpVectors(this.segmentStartTarget, this._tgt, t);
        const fov = segment.fov !== undefined
            ? THREE.MathUtils.lerp(this.segmentStartFov, segment.fov, t)
            : undefined;

        this.applyPose(pos, tgt, fov);
    }

    private updateOrbit(segment: FlyoverOrbitSegment, t: number): void {
        const angleRad = segment.angleDeg * (Math.PI / 180);
        const azimuth = this.orbitStartAzimuth + angleRad * t;

        const pos = this._pos.set(
            segment.center.x + segment.radius * Math.cos(azimuth),
            segment.center.y + segment.height,
            segment.center.z + segment.radius * Math.sin(azimuth),
        );
        const tgt = this._tgt.set(segment.center.x, segment.center.y, segment.center.z);
        const fov = segment.fov !== undefined
            ? THREE.MathUtils.lerp(this.segmentStartFov, segment.fov, t)
            : undefined;

        this.applyPose(pos, tgt, fov);
    }

    private advanceSegment(): void {
        const nextIndex = this.currentSegmentIndex + 1;
        if (nextIndex >= this.segments.length) {
            this.finish();
            return;
        }
        this.initSegment(nextIndex);
    }

    private computeAzimuth(pos: THREE.Vector3, center: Vector3Like): number {
        return Math.atan2(pos.z - center.z, pos.x - center.x);
    }

    // ═══════════════════════════════════════════════════════════════════
    // SHARED HELPERS
    // ═══════════════════════════════════════════════════════════════════

    private applyPose(position: THREE.Vector3, target: THREE.Vector3, fov?: number): void {
        this.camera.position.copy(position);
        this.controls.lookAt(target);
        this.controls.setOrbitTargetOverride(target);

        if (fov !== undefined && fov !== this.camera.fov) {
            this.camera.fov = fov;
            this.camera.updateProjectionMatrix();
        }
    }

    private finish(): void {
        this._active = false;
        this._completed = true;
        this.controls.enabled = this.controlsWasEnabled;
        this.resolvePromise?.();
        this.resolvePromise = null;
    }

    private computeOrbitPose(
        segment: FlyoverOrbitSegment,
        t: number,
    ): { position: THREE.Vector3; target: THREE.Vector3 } {
        const azimuth = (segment.startAzimuth ?? 0) + segment.angleDeg * (Math.PI / 180) * t;
        return {
            position: new THREE.Vector3(
                segment.center.x + segment.radius * Math.cos(azimuth),
                segment.center.y + segment.height,
                segment.center.z + segment.radius * Math.sin(azimuth),
            ),
            target: new THREE.Vector3(segment.center.x, segment.center.y, segment.center.z),
        };
    }
}
